import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { State } from "../../../redux/slices";
import { resetCreateLink } from "../../../redux/slices/data/sharedResult";
import { createSharedResult } from "../../../redux/actions/data/sharedResultAsyncActions";
import { writeClipboard } from "../../../utils/clipboard";

interface ShareLinkModalProps {
  open: boolean;
  onClose: () => void;
  pastActionId: string;
}

const ShareLinkModal: FC<ShareLinkModalProps> = ({
  open,
  onClose,
  pastActionId,
}) => {
  const [t] = useTranslation("global");
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const [copied, setCopied] = useState(false);

  const { user } = useSelector((state: State) => state.Authentication);
  const { locale } = useSelector((state: State) => state.LayoutReducer);
  const { loading, error, shareId } = useSelector(
    (state: State) => state.SharedResultReducer.createLink,
  );

  const shareUrl = shareId
    ? `${window.location.origin}/shared-result/${shareId}`
    : "";

  useEffect(() => {
    if (open && pastActionId) {
      dispatch(
        createSharedResult({
          token: user?.token ?? "",
          locale,
          itemId: pastActionId,
        }),
      );
    }
  }, [open, pastActionId]);

  useEffect(() => {
    if (shareUrl) {
      writeClipboard(shareUrl).catch(() => {});
    }
  }, [shareUrl]);

  const handleCopy = () => {
    writeClipboard(shareUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  const handleClose = () => {
    dispatch(resetCreateLink());
    setCopied(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          outline: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t("Data.SampleView.share.title")}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("Data.SampleView.share.description")}
        </Typography>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {t("Data.SampleView.share.error")}
          </Alert>
        )}

        {shareUrl && !loading && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("Data.SampleView.share.copied_auto")}
            </Alert>
            <TextField
              fullWidth
              value={shareUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopy} edge="end">
                      {copied ? (
                        <CheckIcon color="success" />
                      ) : (
                        <ContentCopyIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="outlined" onClick={handleClose}>
                {t("Data.SampleView.share.close")}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ShareLinkModal;
